import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack, ArrowForward, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { EventFieldChangeData, EventFieldName, FieldChangeData, OrderFieldName, PropelUserModel } from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPages } from '../../common/AccountUtils';

type AuditLogChangeDetailsProps = {
  changes: Array<FieldChangeData | EventFieldChangeData>;
  expanded?: boolean;
  users?: PropelUserModel[];
  hideChangesNumber?: boolean;
};

export const AuditLogChangeDetails = ({
  changes,
  expanded = false,
  users,
  hideChangesNumber = false,
}: AuditLogChangeDetailsProps) => {
  const { authState } = useAuth();
  const { t } = useTranslation();
  const language = useLanguage();
  const [isExpanded, setIsExpanded] = useState(expanded);
  const timeFormat = getTimeFormat(authState?.account || undefined);
  const orderPages = getOrderPages(authState?.account, true);
  if (!changes || changes.length === 0) {
    return null;
  }

  const getFieldLabel = (fieldName: string): string => {
    return t(`auditlog.field_names.${fieldName}`, fieldName);
  };

  const formatValue = (fieldName: OrderFieldName | EventFieldName, value: string | undefined): string => {
    if (value === undefined || value === null) {
      return t('auditlog.change_details.empty_value');
    }
    if (fieldName === OrderFieldName.EMPLOYEE_IDS) {
      if (value === '' || value === '[]') {
        return t('auditlog.change_details.empty_value');
      }

      // Method 1: Remove brackets and split by comma. not sure why but parcing doesnt work here..
      if (value.startsWith('[') && value.endsWith(']')) {
        const content = value.slice(1, -1); // Remove [ and ]
        if (content.trim() === '') {
          return t('auditlog.change_details.empty_value');
        }

        // Split by comma and clean up quotes
        const items = content.split(',').map(
          (item) => item.trim().replace(/^['"]|['"]$/g, ''), // Remove surrounding quotes
        );
        const user_names = items.map((item) => {
          const user = users?.find((user: PropelUserModel) => user.user_id === item);
          if (user) {
            return `${user.first_name} ${user.last_name}`;
          }
          return item;
        });

        return user_names.join(', ');
      }
    }
    if (fieldName === OrderFieldName.TIME || fieldName === OrderFieldName.DURATION) {
      return moment.unix(Number(value)).format(timeFormat);
    }
    if (fieldName === OrderFieldName.ORDER_PAGE_ID) {
      return orderPages.find((o) => o.id === value)?.label[language] || value;
    }
    if (value === '') {
      return t('auditlog.change_details.empty_string');
    }
    return value;
  };

  const renderChangesSummary = () => (
    <Box display="flex" alignItems="center" gap={1} justifyContent="flex-start">
      {!hideChangesNumber && (
        <Chip
          size="small"
          label={t('auditlog.change_details.changes_count', { count: changes.length })}
          color="info"
          variant="outlined"
        />
      )}
      {!expanded && (
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? t('auditlog.change_details.collapse') : t('auditlog.change_details.expand')}>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      )}
    </Box>
  );

  const renderChangesTable = () => {
    const ArrowIcon = language === 'he' ? ArrowBack : ArrowForward;

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mt: 1,
          maxWidth: '100%',
          overflow: 'auto',
        }}>
        <Table size="small" sx={{ minWidth: 'auto', '& .MuiTableCell-root': { padding: '12px 16px' } }}>
          <TableBody>
            {changes.map((change, index) => (
              <TableRow key={`${change.field_name}-${index}`}>
                <TableCell>
                  <Typography variant="body2" fontWeight="700">
                    {getFieldLabel(change.field_name)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{
                      textDecoration: 'line-through',
                      fontStyle: change.old_value ? 'normal' : 'italic',

                      wordBreak: 'break-word',
                    }}>
                    {formatValue(change.field_name, change.old_value)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: 'center', width: '40px' }}>
                  <ArrowIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight="medium"
                    sx={{
                      fontStyle: change.new_value ? 'normal' : 'italic',

                      wordBreak: 'break-word',
                    }}>
                    {formatValue(change.field_name, change.new_value)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start">
      {renderChangesSummary()}
      {isExpanded && <Collapse in={isExpanded || expanded}>{renderChangesTable()}</Collapse>}
    </Box>
  );
};
